import React from "react";
import { BadgeIndianRupee, TrendingUp } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { formatRental } from "@/lib/api";

export const RentalBadge = ({ property }) => {
  const { t } = useLang();
  if (!property.assured_rental_monthly) return null;
  return (
    <div
      data-testid={`property-rental-badge-${property.id}`}
      className="mt-3 inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-lg bg-[#C9A84C] text-[#0B2545] shadow-[0_8px_20px_-10px_rgba(201,168,76,0.9)]"
    >
      <BadgeIndianRupee size={15} strokeWidth={2.75} />
      <span className="text-[11px] uppercase tracking-[0.14em] font-bold">{t.rental.label}:</span>
      <span className="text-sm font-black">
        {formatRental(property.assured_rental_monthly)}
        {t.rental.per_month}
      </span>
    </div>
  );
};

const unitName = (u) => u.unit_label || (u.bhk ? `${u.bhk} BHK` : "Unit");

export const AssuredRentalBox = ({ units, highlightId }) => {
  const { t } = useLang();
  if (!units?.some((u) => u.assured_rental_monthly)) return null;
  return (
    <section
      data-testid="assured-rental-section"
      className="relative overflow-hidden rounded-3xl border-2 border-[#C9A84C] bg-[#0B2545] px-6 py-8 md:px-10 md:py-10"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#C9A84C]/15 blur-3xl" aria-hidden />
      <div className="relative">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C9A84C] font-bold">
          <TrendingUp size={14} strokeWidth={3} /> {t.rental.section_eyebrow}
        </div>
        <h2 className="mt-2 font-serif text-3xl md:text-4xl text-white font-bold">{t.rental.section_title}</h2>
        <p className="mt-2 text-[#E6ECF5] text-sm md:text-base font-medium">{t.rental.section_sub}</p>
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {units.map((u) => (
            <div
              key={u.id}
              data-testid={`assured-rental-row-${u.id}`}
              className={`p-5 rounded-2xl border-2 ${
                u.assured_rental_monthly
                  ? "bg-[#FFFDF5] border-[#C9A84C]"
                  : "bg-white/5 border-white/15"
              } ${u.id === highlightId ? "ring-4 ring-[#C9A84C]/40" : ""}`}
            >
              <div
                className={`text-[11px] uppercase tracking-[0.2em] font-bold ${
                  u.assured_rental_monthly ? "text-[#854D0E]" : "text-[#C9A84C]"
                }`}
              >
                {unitName(u)}
              </div>
              {u.assured_rental_monthly ? (
                <div className="mt-2 font-serif text-3xl md:text-4xl text-[#0B2545] font-bold leading-none">
                  {formatRental(u.assured_rental_monthly)}
                  <span className="ml-1 text-sm font-sans font-bold text-[#854D0E]">{t.rental.per_month_long}</span>
                </div>
              ) : (
                <div className="mt-2 font-serif text-xl text-white font-bold">{t.rental.on_request}</div>
              )}
            </div>
          ))}
        </div>
        <p className="mt-5 text-[11px] text-[#E6ECF5]/70">{t.rental.disclaimer}</p>
      </div>
    </section>
  );
};
